/**
 * Round brilliant cut diamond — crown view
 * Accurate facet geometry: table octagon, 8 kite facets, 8 star facets,
 * 16 upper girdle facets, girdle circle.
 * Used as a large ambient decorative element (rotate slowly).
 */
export function BrilliantCut({
  className = "",
  size = 400,
  opacity = 0.12,
  animated = true,
  stroke: strokeColor = "oklch(0.085 0.004 50)",
}: {
  className?: string;
  size?: number;
  opacity?: number;
  animated?: boolean;
  stroke?: string;
}) {
  const cx = 200;
  const cy = 200;
  const rGirdle = 168;
  const rTable = 58;
  const rStar = 114;       // star facet tips (where star meets girdle boundary)
  const rUpperGirdle = 142; // upper girdle facet inner edge

  const pt = (r: number, deg: number) => {
    const a = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number, number];
  };

  // Table octagon: vertices at 22.5°, 67.5°, … (flat top)
  const tableVerts = Array.from({ length: 8 }, (_, i) => pt(rTable, i * 45 + 22.5));
  const tablePath =
    tableVerts.map((v, i) => `${i === 0 ? "M" : "L"}${v[0].toFixed(2)},${v[1].toFixed(2)}`).join(" ") + " Z";

  // Girdle points: at 0°, 45°, 90°, … (aligned with flat facets between kites)
  const girdlePts = Array.from({ length: 8 }, (_, i) => pt(rGirdle, i * 45));

  // Star facet tips: at same angles as girdle points, but at rStar
  const starTips = Array.from({ length: 8 }, (_, i) => pt(rStar, i * 45));

  // Upper girdle facet inner points (between kite tips, same angle as table vertices)
  const upperGirdleInner = Array.from({ length: 8 }, (_, i) => pt(rUpperGirdle, i * 45 + 22.5));

  // Build path segments
  const segments: string[] = [];

  // Kite facets: each girdle point → its two adjacent table vertices
  for (let i = 0; i < 8; i++) {
    const gp = girdlePts[i];
    const tv1 = tableVerts[i];
    const tv2 = tableVerts[(i + 1) % 8];
    segments.push(
      `M${gp[0].toFixed(2)},${gp[1].toFixed(2)} L${tv1[0].toFixed(2)},${tv1[1].toFixed(2)}`,
      `M${gp[0].toFixed(2)},${gp[1].toFixed(2)} L${tv2[0].toFixed(2)},${tv2[1].toFixed(2)}`,
    );
  }

  // Star facets: each table vertex → its two adjacent star tips (girdle points)
  for (let i = 0; i < 8; i++) {
    const tv = tableVerts[i];
    const st1 = starTips[i];
    const st2 = starTips[(i + 7) % 8]; // previous girdle point
    segments.push(
      `M${tv[0].toFixed(2)},${tv[1].toFixed(2)} L${st1[0].toFixed(2)},${st1[1].toFixed(2)}`,
      `M${tv[0].toFixed(2)},${tv[1].toFixed(2)} L${st2[0].toFixed(2)},${st2[1].toFixed(2)}`,
    );
  }

  // Upper girdle facets: inner points → adjacent girdle points
  for (let i = 0; i < 8; i++) {
    const inner = upperGirdleInner[i];
    const gp1 = girdlePts[i];
    const gp2 = girdlePts[(i + 1) % 8];
    segments.push(
      `M${inner[0].toFixed(2)},${inner[1].toFixed(2)} L${gp1[0].toFixed(2)},${gp1[1].toFixed(2)}`,
      `M${inner[0].toFixed(2)},${inner[1].toFixed(2)} L${gp2[0].toFixed(2)},${gp2[1].toFixed(2)}`,
    );
  }

  const stroke = strokeColor;

  return (
    <svg
      viewBox="0 0 400 400"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      <g opacity={opacity} style={animated ? { transformOrigin: "200px 200px", animation: "spin 90s linear infinite" } : undefined}>
        {/* Girdle circle */}
        <circle cx={cx} cy={cy} r={rGirdle} fill="none" stroke={stroke} strokeWidth="1" />

        {/* Table octagon */}
        <path d={tablePath} fill="none" stroke={stroke} strokeWidth="1.2" />

        {/* All facet lines */}
        <path d={segments.join(" ")} fill="none" stroke={stroke} strokeWidth="0.8" />

        {/* Outer decorative ring */}
        <circle cx={cx} cy={cy} r={rGirdle + 14} fill="none" stroke={stroke} strokeWidth="0.4" strokeDasharray="3 6" />
      </g>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .brilliant-cut { animation: none !important; }
        }
      `}</style>
    </svg>
  );
}
